import React, { useState, useEffect, useCallback } from "react";
import {
  Users, Plus, MessageSquare, Award, Loader2, Trash2, Calendar,
  UserCheck, FileText
} from "lucide-react";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SocialReferralTab = ({ auth, authAxios }) => {
  const [posts, setPosts] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0 });
  const [loading, setLoading] = useState(true);
  const [reviewPost, setReviewPost] = useState(null);
  const [rewardType, setRewardType] = useState("discount_5");
  const [adminNote, setAdminNote] = useState("");
  const [activeSubTab, setActiveSubTab] = useState("posts");
  const [socialLeads, setSocialLeads] = useState([]);
  const [leadStats, setLeadStats] = useState({});
  const [showAddLead, setShowAddLead] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", phone: "", email: "", platform: "instagram", source_detail: "DM" });

  const fetchAll = useCallback(async () => {
    try {
      const [postsRes, lbRes, leadsRes] = await Promise.all([
        authAxios.get(`${API_URL}/api/admin/social-posts`),
        authAxios.get(`${API_URL}/api/admin/social-leaderboard`),
        authAxios.get(`${API_URL}/api/admin/social-leads`)
      ]);
      setPosts(postsRes.data.posts);
      setStats({ total: postsRes.data.total, pending: postsRes.data.pending, approved: postsRes.data.approved });
      setLeaderboard(lbRes.data);
      setSocialLeads(leadsRes.data.leads);
      setLeadStats(leadsRes.data.stats);
    } catch (err) { logger.error(err); }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const reviewAction = async (postId, status) => {
    try {
      await authAxios.put(`${API_URL}/api/admin/social-posts/${postId}`, {
        status, reward_type: status === "approved" ? rewardType : null, admin_note: adminNote
      });
      setReviewPost(null);
      setAdminNote("");
      await fetchAll();
    } catch (err) { alert("Review failed"); }
  };

  const addManualLead = async () => {
    if (!newLead.name) return;
    try {
      await authAxios.post(`${API_URL}/api/admin/social-leads/manual`, newLead);
      setNewLead({ name: "", phone: "", email: "", platform: "instagram", source_detail: "DM" });
      setShowAddLead(false);
      await fetchAll();
    } catch (err) { alert("Failed to add lead"); }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await authAxios.put(`${API_URL}/api/admin/social-leads/${leadId}/status`, { status });
      await fetchAll();
    } catch (err) { logger.error(err); }
  };

  const tierColors = { bronze: "bg-[#CD7F32]", silver: "bg-[#C0C0C0]", gold: "bg-[#FFD700]" };

  if (loading) return <div className="text-center py-12"><Loader2 className="animate-spin mx-auto text-[#E83E8C]" size={32} /></div>;

  return (
    <div className="space-y-6" data-testid="social-referral-tab">
      {/* Sub tabs */}
      <div className="flex gap-2">
        {[
          { key: "posts", label: "Tag & Earn Posts", count: stats.pending },
          { key: "leads", label: "Social Media Leads", count: leadStats.new || 0 },
          { key: "leaderboard", label: "Leaderboard" },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveSubTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${activeSubTab === t.key ? 'bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white shadow-md' : 'bg-white text-[#475569] hover:bg-[#FFFAF5]'}`}
            data-testid={`subtab-${t.key}`}>
            {t.label}
            {t.count > 0 && <span className="px-1.5 py-0.5 bg-white/30 rounded-full text-xs font-bold">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-[#E83E8C]">{stats.total}</p>
          <p className="text-sm text-[#475569]">Total Posts</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-[#FF9F1C]">{stats.pending}</p>
          <p className="text-sm text-[#475569]">Pending Review</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-[#25D366]">{stats.approved}</p>
          <p className="text-sm text-[#475569]">Approved</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-blue-500">{leadStats.total || 0}</p>
          <p className="text-sm text-[#475569]">Social Leads</p>
        </div>
      </div>

      {/* Leaderboard */}
      {activeSubTab === "leaderboard" && leaderboard.length > 0 && (
        <div className="bg-white rounded-3xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-[#1E1B4B] mb-4 flex items-center gap-2"><Award size={20} className="text-[#FFD700]" /> Leaderboard</h2>
          <div className="space-y-2">
            {leaderboard.slice(0, 10).map((l, i) => (
              <div key={l.phone || `lb-${i}`} className="flex items-center gap-4 p-3 rounded-xl border border-gray-100">
                <span className="text-lg font-bold text-[#1E1B4B] w-8 text-center">#{i + 1}</span>
                <div className={`w-8 h-8 rounded-full ${tierColors[l.tier]} flex items-center justify-center text-white text-xs font-bold`}>{l.name[0]}</div>
                <div className="flex-1">
                  <p className="font-semibold text-sm text-[#1E1B4B]">{l.name}</p>
                  <p className="text-xs text-[#94a3b8]">{l.phone} | {l.platforms.join(", ")}</p>
                </div>
                <span className="px-3 py-1 bg-[#E83E8C]/10 text-[#E83E8C] rounded-full text-xs font-bold">{l.post_count} posts</span>
                <span className={`px-2 py-1 rounded-full text-xs font-bold text-white capitalize ${tierColors[l.tier]}`}>{l.tier}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Posts List */}
      {activeSubTab === "posts" && (
      <div className="bg-white rounded-3xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-[#1E1B4B] mb-4 flex items-center gap-2"><MessageSquare size={20} className="text-[#E83E8C]" /> Social Posts ({posts.length})</h2>
        {posts.length === 0 ? (
          <p className="text-sm text-[#94a3b8] text-center py-4">No social post submissions yet.</p>
        ) : (
          <div className="space-y-3">
            {posts.map((p, i) => (
              <div key={p.post_url || `post-${i}`} className="p-4 rounded-2xl border border-gray-100 hover:bg-[#FFFAF5] transition-all" data-testid={`social-post-${i}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center text-white font-bold text-sm">{(p.name || "?")[0]}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#1E1B4B]">{p.name} <span className="text-xs text-[#94a3b8] font-normal">({p.phone})</span></p>
                    <p className="text-xs text-[#94a3b8]">
                      <span className="capitalize font-medium text-[#E83E8C]">{p.platform}</span> | {p.submitted_at?.split("T")[0]}
                    </p>
                    <a href={p.post_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline truncate block">{p.post_url}</a>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.reward_code && <span className="px-2 py-0.5 bg-[#25D366]/10 text-[#25D366] rounded-full text-xs font-bold">{p.reward_code}</span>}
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${p.status === "approved" ? "bg-green-100 text-green-700" : p.status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>{p.status}</span>
                    {p.status === "pending" && (
                      <button onClick={() => { setReviewPost(p); setAdminNote(""); }} className="px-3 py-1.5 bg-[#E83E8C] text-white rounded-lg text-xs font-medium" data-testid={`review-btn-${i}`}>Review</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Social Media Leads */}
      {activeSubTab === "leads" && (
        <div className="bg-white rounded-3xl shadow-lg p-6" data-testid="social-leads-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#1E1B4B] flex items-center gap-2"><Users size={20} className="text-blue-500" /> Social Media Leads ({socialLeads.length})</h2>
            <button onClick={() => setShowAddLead(!showAddLead)} className="px-4 py-2 bg-[#E83E8C] text-white rounded-xl text-sm font-medium flex items-center gap-2 hover:bg-[#d63384]" data-testid="add-social-lead-btn">
              <Plus size={14} /> Add Lead
            </button>
          </div>

          <p className="text-xs text-[#94a3b8] mb-4">Leads captured from Facebook Lead Ads, Instagram DMs, and manual entries. Auto-synced with CRM.</p>

          {showAddLead && (
            <div className="p-4 rounded-2xl bg-[#FFFAF5] border border-[#f0e6d9] mb-4 space-y-3" data-testid="add-lead-form">
              <div className="grid grid-cols-2 gap-3">
                <input value={newLead.name} onChange={e => setNewLead({...newLead, name: e.target.value})} placeholder="Name*" className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none" data-testid="lead-name" />
                <input value={newLead.phone} onChange={e => setNewLead({...newLead, phone: e.target.value})} placeholder="Phone" className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none" data-testid="lead-phone" />
                <input value={newLead.email} onChange={e => setNewLead({...newLead, email: e.target.value})} placeholder="Email" className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none" data-testid="lead-email" />
                <select value={newLead.platform} onChange={e => setNewLead({...newLead, platform: e.target.value})} className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none">
                  <option value="instagram">Instagram</option>
                  <option value="facebook">Facebook</option>
                </select>
              </div>
              <div className="flex gap-2 items-center">
                <select value={newLead.source_detail} onChange={e => setNewLead({...newLead, source_detail: e.target.value})} className="px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none">
                  <option value="DM">DM Enquiry</option>
                  <option value="comment">Post Comment</option>
                  <option value="mention">Story Mention</option>
                  <option value="ad">Ad Response</option>
                  <option value="other">Other</option>
                </select>
                <button onClick={addManualLead} className="px-4 py-2 bg-[#25D366] text-white rounded-xl text-sm font-medium" data-testid="save-lead-btn">Save</button>
                <button onClick={() => setShowAddLead(false)} className="px-4 py-2 bg-gray-100 text-gray-500 rounded-xl text-sm">Cancel</button>
              </div>
            </div>
          )}

          {socialLeads.length === 0 ? (
            <p className="text-sm text-[#94a3b8] text-center py-4">No social media leads yet. Add manually or connect Facebook Lead Ads.</p>
          ) : (
            <div className="space-y-2">
              {socialLeads.map((l, i) => (
                <div key={l.id || l.phone || `slead-${i}`} className="flex items-center gap-4 p-3 rounded-xl border border-gray-100 hover:bg-[#FFFAF5]" data-testid={`social-lead-${i}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${l.platform === 'instagram' ? 'bg-gradient-to-br from-[#833AB4] to-[#E1306C]' : 'bg-[#1877F2]'}`}>
                    {(l.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#1E1B4B]">{l.name || "Unknown"}</p>
                    <p className="text-xs text-[#94a3b8]">{l.phone || l.email || l.sender_id || "No contact"} | <span className="capitalize">{l.platform}</span> {l.source && `/ ${l.source}`}</p>
                  </div>
                  <span className="text-xs text-[#94a3b8]">{l.created_at?.split("T")[0]}</span>
                  <select value={l.status} onChange={e => updateLeadStatus(l.id, e.target.value)}
                    className={`px-2 py-1 rounded-lg text-xs font-medium border-0 outline-none cursor-pointer ${l.status === 'new' ? 'bg-blue-100 text-blue-700' : l.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' : l.status === 'converted' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                    data-testid={`lead-status-${i}`}>
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Review Modal */}
      {reviewPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setReviewPost(null)}>
          <div className="bg-white rounded-3xl p-6 w-[440px] max-w-[90vw]" onClick={e => e.stopPropagation()} data-testid="review-modal">
            <h3 className="text-lg font-bold text-[#1E1B4B] mb-2">Review Post</h3>
            <p className="text-sm text-[#475569] mb-1">{reviewPost.name} | <span className="capitalize">{reviewPost.platform}</span></p>
            <a href={reviewPost.post_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-500 hover:underline block mb-4 truncate">{reviewPost.post_url}</a>

            <div className="mb-4">
              <label className="text-sm font-medium text-[#1E1B4B] block mb-2">Reward Tier</label>
              <div className="flex gap-2">
                {[
                  { key: "discount_5", label: "5% Off" },
                  { key: "discount_10", label: "10% Off" },
                  { key: "free_addon", label: "Free Add-on" },
                ].map(r => (
                  <button key={r.key} onClick={() => setRewardType(r.key)}
                    className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${rewardType === r.key ? 'bg-[#E83E8C] text-white' : 'bg-gray-100 text-gray-500'}`}
                  >{r.label}</button>
                ))}
              </div>
            </div>

            <input value={adminNote} onChange={e => setAdminNote(e.target.value)} placeholder="Admin note (optional)" className="w-full px-3 py-2 rounded-xl border text-sm mb-4 focus:border-[#E83E8C] outline-none" />

            <div className="flex gap-3">
              <button onClick={() => reviewAction(reviewPost.id, "approved")} className="flex-1 py-2.5 bg-[#25D366] text-white rounded-xl text-sm font-bold hover:bg-[#1da851]" data-testid="approve-btn">Approve & Reward</button>
              <button onClick={() => reviewAction(reviewPost.id, "rejected")} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-bold hover:bg-red-600" data-testid="reject-btn">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialReferralTab;
